import { useState } from 'react';
import { BottomSheet } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { rangeLabel } from './calendarLabels';
import { MODE_LABEL } from './CalendarPageHeader';
import type { CalendarMode } from './calendarSearch';
import { CalendarToolbar, type CalendarToolbarProps } from './CalendarToolbar';
import type { CalendarView } from './useCalendarView';
import { ViewRangeCalendar } from './ViewRangeCalendar';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: CalendarView;
  today: string;
  modes: CalendarMode[];
  onModeChange: (mode: CalendarMode) => void;
  toolbar: Omit<CalendarToolbarProps, 'layout'>;
}

const eyebrow = 'text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]';

/**
 * „Filter und Ansicht“ am Handy (Board „Belegung · Handy“): Modus, Zeitraum mit Heute und freier
 * Wahl, dann Aufenthalte, Apartment und Kanäle. Jede Wahl wirkt sofort; „Anzeigen“ schließt.
 * Der Zeitraumkalender klappt im Blatt auf – ein Popover im Dialog wäre nicht bedienbar.
 */
export function CalendarFilterSheet(props: Props) {
  const { view, modes } = props;
  const [picking, setPicking] = useState(false);
  return (
    <BottomSheet
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Filter und Ansicht"
      description="Gilt für diese Ansicht"
      footer={
        <button
          type="button"
          onClick={() => props.onOpenChange(false)}
          className="rounded-pill bg-ink h-[52px] w-full text-[15px] font-bold text-white"
        >
          Anzeigen
        </button>
      }
    >
      <div className="flex flex-col gap-3">
        {modes.length > 1 && (
          <>
            <span className={eyebrow}>Ansicht</span>
            <div
              role="radiogroup"
              aria-label="Was zeigen"
              className="rounded-pill bg-card-alt grid gap-1 p-1"
              style={{ gridTemplateColumns: `repeat(${modes.length}, minmax(0, 1fr))` }}
            >
              {modes.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={view.mode === mode}
                  onClick={() => props.onModeChange(mode)}
                  className={cn(
                    'rounded-pill h-11 text-[13px]',
                    view.mode === mode
                      ? 'bg-card text-ink font-bold shadow-sm'
                      : 'text-ink-soft font-semibold',
                  )}
                >
                  {MODE_LABEL[mode]}
                </button>
              ))}
            </div>
          </>
        )}
        <span className={eyebrow}>Zeitraum</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => view.goToday(props.today)}
            className="border-line rounded-pill h-11 flex-none border px-4 text-[13px] font-bold"
          >
            Heute
          </button>
          <button
            type="button"
            aria-expanded={picking}
            onClick={() => setPicking((value) => !value)}
            className="border-line rounded-control h-11 min-w-0 grow border px-3.5 text-left text-[13px] font-semibold"
          >
            Zeitraum wählen: {rangeLabel(view.from, view.to)}
          </button>
        </div>
        {picking && (
          <ViewRangeCalendar
            initial={{ from: view.from, to: view.to }}
            months={1}
            applyLabel="Zeitraum anzeigen"
            onCancel={() => setPicking(false)}
            onApply={(range) => {
              view.showRange(range);
              setPicking(false);
            }}
          />
        )}
        {view.mode === 'apartments' && <CalendarToolbar {...props.toolbar} layout="sheet" />}
      </div>
    </BottomSheet>
  );
}
